import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SISTEMAS = [
  { value: 'pastoril', label: 'Pastoril', color: 'bg-green-100 text-green-800' },
  { value: 'semi_intensivo', label: 'Semi-intensivo', color: 'bg-blue-100 text-blue-800' },
  { value: 'confinamiento', label: 'Confinamiento', color: 'bg-purple-100 text-purple-800' },
];
const SUPLEMENTACIONES = [
  { value: 'sales_minerales', label: 'Sales Minerales', desc: 'Ca, P, Mg, Zn - Base' },
  { value: 'proteica', label: 'Proteica', desc: 'Harina soja/girasol - Recria' },
  { value: 'energetica', label: 'Energetica', desc: 'Grano maiz/sorgo - Engorde' },
  { value: 'balanceado_completo', label: 'Balanceado Completo', desc: '65-80% grano + proteina + minerales' },
  { value: 'mixta', label: 'Mixta', desc: 'Combinacion adaptada' },
];
const ETAPAS = [
  { value: 'recepcion', label: 'Recepcion (0-28d)', color: 'text-yellow-700' },
  { value: 'crecimiento', label: 'Crecimiento (28-90d)', color: 'text-blue-700' },
  { value: 'terminacion', label: 'Terminacion (90-180d)', color: 'text-green-700' },
  { value: 'mantenimiento', label: 'Mantenimiento', color: 'text-gray-600' },
];

const LotesEngordePage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('lista');
  const [lotes, setLotes] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [selectedLote, setSelectedLote] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create form
  const [form, setForm] = useState({
    nombre: '', sistema: 'semi_intensivo', tipo_suplementacion: 'sales_minerales',
    etapa: 'crecimiento', peso_objetivo: '', capacidad_maxima: '',
    costo_diario_animal: '', detalle_suplementacion: '', dias_rotacion: '90', observaciones: '',
  });

  // Assign animals
  const [showAssign, setShowAssign] = useState(false);
  const [searchVID, setSearchVID] = useState('');
  const [foundAnimals, setFoundAnimals] = useState([]);
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Move animals
  const [showMove, setShowMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [moveAnimals, setMoveAnimals] = useState([]);

  const loadData = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    try {
      const res = await api.get(`/lotes-engorde/resumen/${user.establecimiento_id}`);
      setLotes(res.data.lotes || []);
      setResumen(res.data);
    } catch {} finally { setLoading(false); }
  }, [user?.establecimiento_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadDashboard = async (loteId) => {
    try {
      const res = await api.get(`/lotes-engorde/${loteId}/dashboard`);
      setDashboard(res.data);
      setSelectedLote(res.data.lote);
      setView('dashboard');
    } catch { setError('Error cargando dashboard del lote'); }
  };

  const handleCreate = async () => {
    setError(''); setSuccess('');
    try {
      const payload = {
        establecimiento_id: user.establecimiento_id,
        nombre: form.nombre,
        sistema: form.sistema,
        tipo_suplementacion: form.tipo_suplementacion,
        etapa: form.etapa,
        peso_objetivo: form.peso_objetivo ? parseFloat(form.peso_objetivo) : null,
        capacidad_maxima: form.capacidad_maxima ? parseInt(form.capacidad_maxima) : null,
        costo_diario_animal: form.costo_diario_animal ? parseFloat(form.costo_diario_animal) : null,
        detalle_suplementacion: form.detalle_suplementacion || null,
        dias_rotacion: parseInt(form.dias_rotacion) || 90,
        observaciones: form.observaciones || null,
      };
      await api.post('/lotes-engorde/', payload);
      setSuccess('Lote creado exitosamente');
      setShowCreateForm(false);
      setForm({ nombre: '', sistema: 'semi_intensivo', tipo_suplementacion: 'sales_minerales', etapa: 'crecimiento', peso_objetivo: '', capacidad_maxima: '', costo_diario_animal: '', detalle_suplementacion: '', dias_rotacion: '90', observaciones: '' });
      loadData();
    } catch (err) { setError(err.response?.data?.detail || 'Error al crear lote'); }
  };

  const handleSearchAnimals = async () => {
    if (!searchVID.trim()) return;
    try {
      const res = await api.get(`/animales/?establecimiento_id=${user.establecimiento_id}&buscar=${searchVID}&page_size=20`);
      setFoundAnimals(res.data.data || []);
    } catch {}
  };

  const handleAssign = async () => {
    if (!selectedAnimals.length || !selectedLote) return;
    setAssigning(true);
    try {
      await api.post(`/lotes-engorde/${selectedLote.id}/asignar`, selectedAnimals);
      setSuccess(`${selectedAnimals.length} animales asignados`);
      setSelectedAnimals([]); setShowAssign(false); setFoundAnimals([]);
      loadDashboard(selectedLote.id);
    } catch (err) { setError(err.response?.data?.detail || 'Error asignando'); }
    finally { setAssigning(false); }
  };

  const handleMove = async () => {
    if (!moveAnimals.length || !moveTarget || !selectedLote) return;
    try {
      await api.post(`/lotes-engorde/mover/${selectedLote.id}/${moveTarget}`, moveAnimals);
      setSuccess('Animales movidos exitosamente');
      setShowMove(false); setMoveAnimals([]);
      loadDashboard(selectedLote.id);
    } catch (err) { setError(err.response?.data?.detail || 'Error moviendo'); }
  };

  const suplLabel = (val) => SUPLEMENTACIONES.find(s => s.value === val)?.label || val;
  const sistLabel = (val) => SISTEMAS.find(s => s.value === val)?.label || val;
  const etapaLabel = (val) => ETAPAS.find(e => e.value === val)?.label || val;
  const sistColor = (val) => SISTEMAS.find(s => s.value === val)?.color || 'bg-gray-100 text-gray-800';

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="lotes-engorde-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lotes de Engorde</h1>
          <p className="text-sm text-gray-500">Gestiona corrales, suplementacion y movimiento de animales</p>
        </div>
        <div className="flex gap-2">
          {view === 'dashboard' && (
            <button onClick={() => { setView('lista'); setSelectedLote(null); setDashboard(null); }}
              className="px-3 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50" data-testid="back-to-lotes">
              Volver a lotes
            </button>
          )}
          <button onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition" data-testid="crear-lote-btn">
            + Nuevo Lote
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="lote-error">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="lote-success">{success}</div>}

      {/* ─── CREATE FORM ─── */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4" data-testid="create-lote-form">
          <h3 className="text-lg font-bold text-gray-900">Nuevo Lote / Corral</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del lote *</label>
              <input type="text" value={form.nombre} onChange={e => setForm(p => ({...p, nombre: e.target.value}))}
                placeholder="Ej: Corral 1 - Recepcion" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-nombre" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sistema de engorde</label>
              <select value={form.sistema} onChange={e => setForm(p => ({...p, sistema: e.target.value}))}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-sistema">
                {SISTEMAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo suplementacion</label>
              <select value={form.tipo_suplementacion} onChange={e => setForm(p => ({...p, tipo_suplementacion: e.target.value}))}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-suplementacion">
                {SUPLEMENTACIONES.map(s => <option key={s.value} value={s.value}>{s.label} — {s.desc}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Etapa</label>
              <select value={form.etapa} onChange={e => setForm(p => ({...p, etapa: e.target.value}))}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-etapa">
                {ETAPAS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Peso objetivo (kg)</label>
              <input type="number" value={form.peso_objetivo} onChange={e => setForm(p => ({...p, peso_objetivo: e.target.value}))}
                placeholder="Ej: 350" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-peso-obj" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Costo diario/animal (USD)</label>
              <input type="number" step="0.01" value={form.costo_diario_animal} onChange={e => setForm(p => ({...p, costo_diario_animal: e.target.value}))}
                placeholder="Ej: 1.50" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-costo" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacidad maxima</label>
              <input type="number" value={form.capacidad_maxima} onChange={e => setForm(p => ({...p, capacidad_maxima: e.target.value}))}
                placeholder="Ej: 100" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-capacidad" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dias rotacion</label>
              <input type="number" value={form.dias_rotacion} onChange={e => setForm(p => ({...p, dias_rotacion: e.target.value}))}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-rotacion" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Detalle suplementacion</label>
              <input type="text" value={form.detalle_suplementacion} onChange={e => setForm(p => ({...p, detalle_suplementacion: e.target.value}))}
                placeholder="Ej: Maiz 70%, Soja 20%, Minerales 10%" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lote-detalle" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-600">Cancelar</button>
            <button onClick={handleCreate} disabled={!form.nombre.trim()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50" data-testid="submit-lote-btn">
              Crear Lote
            </button>
          </div>
        </div>
      )}

      {/* ─── LISTA DE LOTES ─── */}
      {view === 'lista' && (
        <div className="space-y-4">
          {/* Summary cards */}
          {resumen && resumen.total_lotes > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-xs text-gray-500">Lotes Activos</p><p className="text-2xl font-bold text-gray-900">{resumen.total_lotes}</p></div>
              <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-xs text-gray-500">Animales en Lotes</p><p className="text-2xl font-bold text-gray-900">{resumen.total_animales_en_lotes}</p></div>
              <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-xs text-gray-500">Costo Acumulado</p><p className="text-2xl font-bold text-green-700">${resumen.costo_total_acumulado?.toFixed(2)}</p></div>
              <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-xs text-gray-500">Sistemas</p><p className="text-lg font-bold text-gray-700">{[...new Set(lotes.map(l => l.sistema))].map(sistLabel).join(', ')}</p></div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : lotes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-gray-700 mb-2">Sin lotes de engorde</h3>
              <p className="text-sm text-gray-500 mb-4">Crea tu primer lote para organizar animales por corral y suplementacion</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {lotes.map(lote => (
                <div key={lote.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition cursor-pointer"
                  onClick={() => loadDashboard(lote.id)} data-testid={`lote-card-${lote.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900">{lote.nombre}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sistColor(lote.sistema)}`}>{sistLabel(lote.sistema)}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Suplementacion</span><span className="font-medium">{suplLabel(lote.tipo_suplementacion)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Etapa</span><span className="font-medium">{etapaLabel(lote.etapa)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Animales</span><span className="font-bold text-green-700">{lote.total_animales || 0}</span></div>
                    {lote.peso_objetivo && <div className="flex justify-between"><span className="text-gray-500">Peso objetivo</span><span className="font-medium">{lote.peso_objetivo} kg</span></div>}
                    {lote.peso_promedio > 0 && <div className="flex justify-between"><span className="text-gray-500">Peso promedio</span><span className="font-medium">{lote.peso_promedio} kg</span></div>}
                    {lote.costo_acumulado > 0 && <div className="flex justify-between"><span className="text-gray-500">Costo acumulado</span><span className="font-bold text-amber-700">${lote.costo_acumulado.toFixed(2)}</span></div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── DASHBOARD LOTE ─── */}
      {view === 'dashboard' && dashboard && (
        <div className="space-y-5">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm p-5" data-testid="lote-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedLote?.nombre}</h2>
                <div className="flex gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sistColor(selectedLote?.sistema)}`}>{sistLabel(selectedLote?.sistema)}</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">{suplLabel(selectedLote?.tipo_suplementacion)}</span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">{etapaLabel(selectedLote?.etapa)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowAssign(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700" data-testid="asignar-btn">
                  + Asignar Animales
                </button>
                {dashboard.total_listos > 0 && (
                  <button onClick={() => { setShowMove(true); setMoveAnimals(dashboard.listos_para_mover.map(a => a.animal_id)); }}
                    className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 animate-pulse" data-testid="mover-listos-btn">
                    Mover {dashboard.total_listos} listos
                  </button>
                )}
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Animales</p><p className="text-xl font-bold">{dashboard.total_animales}</p></div>
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Peso promedio</p><p className="text-xl font-bold">{dashboard.peso_promedio} kg</p></div>
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">GMD promedio</p><p className="text-xl font-bold">{dashboard.gmd_promedio} kg/d</p></div>
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Dias promedio</p><p className="text-xl font-bold">{dashboard.dias_promedio}d</p></div>
              <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Costo acumulado</p><p className="text-xl font-bold text-amber-700">${dashboard.costo_acumulado}</p></div>
            </div>

            {selectedLote?.peso_objetivo && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Progreso hacia peso objetivo: <strong>{selectedLote.peso_objetivo} kg</strong></span>
                  <span className="font-bold text-green-700">{dashboard.peso_promedio > 0 ? Math.min(100, Math.round(dashboard.peso_promedio / selectedLote.peso_objetivo * 100)) : 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{width: `${Math.min(100, Math.round((dashboard.peso_promedio / selectedLote.peso_objetivo) * 100))}%`}} />
                </div>
              </div>
            )}
          </div>

          {/* Listos para mover */}
          {dashboard.total_listos > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="listos-mover">
              <h3 className="font-bold text-amber-800 mb-2">Animales que alcanzaron peso objetivo ({selectedLote?.peso_objetivo} kg)</h3>
              <div className="flex flex-wrap gap-2">
                {dashboard.listos_para_mover.map(a => (
                  <span key={a.animal_id} className="px-3 py-1 bg-amber-200 text-amber-900 rounded-full text-xs font-mono font-bold">
                    VID {a.VID} — {Number(a.peso).toFixed(2)}kg
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Animals table */}
          {dashboard.animales.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="lote-animales-table">
              <div className="p-4 border-b"><h3 className="font-bold text-gray-900">Animales en el lote ({dashboard.total_animales})</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">VID</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Sexo</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Propietario</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Peso Ingreso</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Peso Actual</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Ganancia</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">GMD</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Dias</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {dashboard.animales.map(a => {
                      const ganancia = a.peso_actual && a.peso_ingreso ? (a.peso_actual - a.peso_ingreso).toFixed(1) : '-';
                      const reachedTarget = selectedLote?.peso_objetivo && a.peso_actual >= selectedLote.peso_objetivo;
                      return (
                        <tr key={a.id} className={`hover:bg-gray-50 ${reachedTarget ? 'bg-amber-50' : ''}`}>
                          <td className="px-4 py-2 font-mono font-bold text-blue-700">{a.VID}</td>
                          <td className="px-4 py-2 text-gray-600">{a.sexo}</td>
                          <td className="px-4 py-2 text-gray-600">{a.propietario || '-'}</td>
                          <td className="px-4 py-2 text-right text-gray-600">{a.peso_ingreso ? `${a.peso_ingreso} kg` : '-'}</td>
                          <td className="px-4 py-2 text-right font-bold">{a.peso_actual ? `${a.peso_actual} kg` : '-'}</td>
                          <td className={`px-4 py-2 text-right font-medium ${parseFloat(ganancia) > 0 ? 'text-green-700' : 'text-gray-500'}`}>{ganancia !== '-' ? `+${ganancia} kg` : '-'}</td>
                          <td className="px-4 py-2 text-right">{a.gmd ? `${a.gmd} kg/d` : '-'}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{a.dias_en_lote}d</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Assign Modal */}
          {showAssign && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="assign-modal">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-gray-900">Asignar Animales al Lote</h3>
                  <button onClick={() => { setShowAssign(false); setFoundAnimals([]); setSelectedAnimals([]); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                </div>
                <div className="flex gap-2">
                  <input type="text" value={searchVID} onChange={e => setSearchVID(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchAnimals()}
                    placeholder="Buscar por VID o propietario..." className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="assign-search" />
                  <button onClick={handleSearchAnimals} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm" data-testid="assign-search-btn">Buscar</button>
                </div>
                {foundAnimals.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {foundAnimals.map(a => (
                      <label key={a.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                        <input type="checkbox" checked={selectedAnimals.includes(a.id)}
                          onChange={e => setSelectedAnimals(prev => e.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} />
                        <span className="font-mono font-bold text-sm text-blue-700">VID {a.VID}</span>
                        <span className="text-xs text-gray-500">{a.sexo} — {a.raza} {a.propietario ? `— ${a.propietario}` : ''}</span>
                      </label>
                    ))}
                  </div>
                )}
                {selectedAnimals.length > 0 && (
                  <button onClick={handleAssign} disabled={assigning}
                    className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50" data-testid="confirm-assign-btn">
                    {assigning ? 'Asignando...' : `Asignar ${selectedAnimals.length} animales`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Move Modal */}
          {showMove && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="move-modal">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <h3 className="font-bold text-gray-900">Mover {moveAnimals.length} animales</h3>
                <select value={moveTarget} onChange={e => setMoveTarget(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="move-target-select">
                  <option value="">Seleccionar lote destino...</option>
                  {lotes.filter(l => l.id !== selectedLote?.id).map(l => (
                    <option key={l.id} value={l.id}>{l.nombre} ({suplLabel(l.tipo_suplementacion)})</option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <button onClick={() => { setShowMove(false); setMoveAnimals([]); }} className="flex-1 py-2 border rounded-lg text-sm">Cancelar</button>
                  <button onClick={handleMove} disabled={!moveTarget}
                    className="flex-1 py-2 bg-amber-500 text-white rounded-lg font-semibold text-sm disabled:opacity-50" data-testid="confirm-move-btn">
                    Mover
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LotesEngordePage;
